// ─────────────────────────────────────────────────────────────────────────────
// FIREBASE STORAGE RULES REMINDER
// Add the following to your Firebase Storage rules in the Firebase console:
//
//   match /photos/{allPaths=**} {
//     allow read:   if request.auth != null;
//     allow write:  if request.auth != null;
//     allow delete: if request.auth != null;
//   }
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { addDoc, onSnapshot, deleteDoc, doc, collection, query, where, serverTimestamp } from "firebase/firestore";
import { db, storage } from "./firebase";

// ─── STANDALONE: upload a single photo ───────────────────────────────────────
export async function uploadPhoto(file, entityType, entityId, category, notes, uploadedBy, takenAt) {
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("Fichier trop volumineux (max 10 MB)");
  }
  const timestamp = Date.now();
  const safeFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `photos/${entityType}/${entityId}/${category}/${timestamp}_${safeFilename}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);
  const docRef = await addDoc(collection(db, "photos"), {
    entityType,
    entityId,
    category,
    notes: notes || "",
    uploadedBy,
    storagePath,
    downloadURL,
    filename: file.name,
    fileSize: file.size,
    takenAt: takenAt || new Date().toISOString().slice(0, 10),
    uploadedAt: serverTimestamp(),
  });
  return { id: docRef.id };
}

// ─── STANDALONE: delete a photo (Storage + Firestore) ────────────────────────
export async function deletePhoto(photo) {
  const storageRef = ref(storage, photo.storagePath);
  await deleteObject(storageRef);
  await deleteDoc(doc(db, "photos", photo.id));
}

// ─── HOOK: real-time photos for an entity ─────────────────────────────────────
// Usage: const [photos, upload, del, uploading] = usePhotos("job", jobId);
export function usePhotos(entityType, entityId) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!entityType || !entityId) { setPhotos([]); return; }
    const q = query(
      collection(db, "photos"),
      where("entityType", "==", entityType),
      where("entityId", "==", entityId)
    );
    const unsub = onSnapshot(q, snap => {
      setPhotos(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0))
      );
    });
    return unsub;
  }, [entityType, entityId]);

  const upload = async (file, category, notes, uploadedBy, takenAt) => {
    setUploading(true);
    try {
      return await uploadPhoto(file, entityType, entityId, category, notes, uploadedBy, takenAt);
    } finally {
      setUploading(false);
    }
  };

  const del = async (photo) => {
    await deletePhoto(photo);
  };

  return [photos, upload, del, uploading];
}
